/* global debounce */

if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    /**
     * Every Algolia query is billed, and the dropdown queries on each pause in
     * typing. A single character matches almost everything and is replaced a
     * moment later, so it isn't worth a request. Two still lets sizes such as
     * "2y" or "3m" through.
     */
    static ALGOLIA_MIN_CHARS = 2;

    /** Slightly longer than the theme's 300ms, so fewer mid-word queries. */
    static ALGOLIA_DEBOUNCE = 400;

    constructor() {
      super();
      this.cachedResults = {};
      this.algoliaCache = {};
      this.input = this.querySelector('.js-search-input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = document.getElementById('product_type_input');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.results = this.querySelector('.js-search-results');
      this.overlay = this.querySelector('.js-search-overlay');
      this.statusEl = this.querySelector('.js-search-status');
      this.loadingText = this.getAttribute('data-loading-text');
      this.addListeners();
    }

    /**
     * Triggers when the web component is removed from the DOM
     */
    disconnectedCallback() {
      this.close();
    }

    addListeners() {
      this.input.addEventListener('focus', this.handleFocus.bind(this));
      const wait = PredictiveSearch.usesAlgolia() ? PredictiveSearch.ALGOLIA_DEBOUNCE : undefined;
      this.input.addEventListener('input', debounce(this.handleInput.bind(this), wait));
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.handleProductTypeChange.bind(this));
      }
      this.querySelector('.search').addEventListener('submit', this.handleSubmit.bind(this));
    }

    /**
     * Whether the dropdown is configured to run on Algolia. Read from the
     * inline config rather than the client, which may not have loaded yet.
     * @returns {boolean}
     */
    static usesAlgolia() {
      const { algolia } = window.theme;
      return Boolean(algolia && algolia.enabled && algolia.surfaces.predictive);
    }

    /**
     * Gets the value of the search input field.
     * @returns {string}
     */
    getQuery() {
      return this.input.value.trim();
    }

    /**
     * Handles search form submit.
     * @param {object} evt - Event object.
     */
    handleSubmit(evt) {
      if (
        !this.getQuery().length
        || this.querySelector('.predictive-search__item[aria-selected="true"]:not(.js-submit)')
      ) {
        evt.preventDefault();
      }
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput() {
      // autocomplete=off prevents browser from restoring value after using back button in Safari
      this.input.setAttribute('value', this.input.value);

      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        this.close();
        return;
      }

      this.getResults(searchTerm);
    }

    /**
     * Handles a change of product type
     * @param {object} evt - Event object.
     */
    handleProductTypeChange(evt) {
      this.productTypeInput.value = evt.detail.selectedValue;
      const query = this.getQuery();
      if (query.length > 0) {
        this.getResults(query);
      }
    }

    /**
     * Handles 'focus' events on the search field.
     */
    handleFocus() {
      const searchTerm = this.getQuery();
      if (!searchTerm.length) return;

      if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getResults(searchTerm);
      }
    }

    /**
     * Handles 'keydown' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      // Let tabs script handle keydown events on the tab buttons.
      if (evt.target.matches('.tablist__tab')) return;

      switch (evt.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();

          if (this.hasAttribute('open')) {
            this.handleResultsNav(evt.key);
          }
          break;

        case 'Enter':
          this.selectOption();
          break;

        case 'Escape':
          this.close();
          break;

        // no default
      }
    }

    /**
     * Handles 'keyup' events on the custom element.
     */
    handleKeyup() {
      // If search field is empty after key press, close the results.
      if (!this.getQuery().length) this.close();
    }

    /**
     * Handles keyboard navigation of the results.
     * @param {string} key - Key pressed.
     */
    handleResultsNav(key) {
      const selectedResult = this.querySelector(
        '[role="tabpanel"]:not([hidden]) [aria-selected="true"]'
      );
      const allResults = this.querySelectorAll(
        '[role="tabpanel"]:not([hidden]) .predictive-search__item'
      );
      let resultToSelect = allResults[0];

      if (key === 'ArrowUp') {
        if (!selectedResult) return;

        // Select the next result up or the last one if we've reached the top of the list.
        resultToSelect = selectedResult.previousElementSibling || allResults[allResults.length - 1];
      } else if (key === 'ArrowDown') {
        if (selectedResult) {
          // Select the next result down or the first one if we've reached the bottom of the list.
          resultToSelect = selectedResult.nextElementSibling || allResults[0];
        }
      }

      this.statusEl.textContent = '';

      if (resultToSelect) {
        // If the selected item didn't change, do nothing.
        if (resultToSelect === selectedResult) return;

        resultToSelect.setAttribute('aria-selected', 'true');
        if (selectedResult) selectedResult.setAttribute('aria-selected', 'false');

        this.setLiveRegionText(resultToSelect.textContent);
        this.input.setAttribute('aria-activedescendant', resultToSelect.id);
      }
    }

    /**
     * Selects a result
     */
    selectOption() {
      const selectedResult = this.querySelector('[aria-selected="true"] > .js-search-link');
      if (selectedResult) selectedResult.click();
    }

    /**
     * Gets the results for a search term from Algolia.
     * @param {string} searchTerm - Search query.
     * @returns {Promise<string|null>} Results markup, or null to fall back to Shopify.
     */
    async getAlgoliaResults(searchTerm) {
      const { algoliaClient, algoliaTemplates, algolia } = window.theme;
      if (!algoliaClient || !algoliaClient.isEnabled('predictive')) return null;

      const hitsPerPage = algolia.predictive.limit;

      // In "Variant" indexing mode one product yields many records, so we
      // over-fetch and collapse them down to `hitsPerPage` products below. In
      // product mode one record is already one product.
      const overfetch = algolia.indexingMode === 'variant' ? 5 : 1;
      const productParams = {
        query: searchTerm,
        hitsPerPage: hitsPerPage * overfetch,
        distinct: true,
        // Returns a queryID, so a click from the dropdown counts as search
        // attributed rather than anonymous.
        clickAnalytics: true
      };

      // The header's product type dropdown becomes a facet filter.
      const productType = this.productTypeInput ? this.productTypeInput.value : '';
      const facetFilters = [];
      if (productType !== '') facetFilters.push([`product_type:${productType}`]);
      if (algolia.hideSoldOut) facetFilters.push(['inventory_available:true']);
      if (facetFilters.length) productParams.facetFilters = facetFilters;

      // Backspacing over a character, or retyping a word, would otherwise bill
      // the same query again.
      const cacheKey = `${productType}|${searchTerm.toLowerCase()}`;
      const { algoliaInsights } = window.theme;
      const cached = this.algoliaCache[cacheKey];
      if (cached) {
        if (algoliaInsights) algoliaInsights.rememberSearch(cached.queryID, cached.products);
        return cached.markup;
      }

      // Cancel the in-flight request - the query has moved on.
      if (this.algoliaAbortController) this.algoliaAbortController.abort('Query changed');
      this.algoliaAbortController = new AbortController();
      const { signal } = this.algoliaAbortController;

      // Only query the indices the Algolia app actually maintains - a missing
      // index makes the whole multi-query request fail with a 404.
      //
      // Collections are not among them: Algolia bills each index in a request
      // separately, while Shopify's own predictive search matches collection
      // titles just as well for free.
      const requests = [{ indexName: algolia.indices.products, params: productParams }];
      const resourceOrder = ['pages', 'articles'];

      resourceOrder.forEach((resource) => {
        if (!algolia.resources[resource]) return;
        requests.push({
          indexName: algolia.indices[resource],
          params: { query: searchTerm, hitsPerPage }
        });
      });

      const [results, collections] = await Promise.all([
        algoliaClient.search(requests, signal),
        algolia.resources.collections ? this.getShopifyCollections(searchTerm, hitsPerPage, signal) : []
      ]);
      if (!results) return null;

      const groups = {
        products: algoliaTemplates.dedupeProducts(results[0] ? results[0].hits : [], hitsPerPage),
        collections
      };

      // Merged rather than reset: on the search page the dropdown and the
      // results grid are both on screen, and clearing here would strip the
      // grid's rows of their attribution.
      const queryID = results[0] ? results[0].queryID : undefined;
      if (algoliaInsights && queryID) {
        algoliaInsights.rememberSearch(queryID, groups.products);
      }
      let resultIndex = 1;
      resourceOrder.forEach((resource) => {
        const enabled = algolia.resources[resource];
        groups[resource] = enabled && results[resultIndex] ? results[resultIndex].hits : [];
        if (enabled) resultIndex += 1;
      });

      // Namespace the tab/panel ids to this instance, as the Shopify path does.
      const markup = algoliaTemplates.renderPredictiveResults(groups, searchTerm)
        .replace(/psearch/g, this.input.id);

      this.algoliaCache[cacheKey] = { markup, queryID, products: groups.products };
      return markup;
    }

    /**
     * Gets matching collections from Shopify's predictive search, in the shape
     * the Algolia dropdown renders.
     *
     * Collections are secondary in the dropdown, so any failure here yields an
     * empty list rather than taking the product results down with it.
     * @param {string} searchTerm - Search query.
     * @param {number} limit - Maximum number of collections.
     * @param {AbortSignal} signal - Abort signal shared with the product query.
     * @returns {Promise<Array>} Objects with `handle`, `title` and `image`.
     */
    async getShopifyCollections(searchTerm, limit, signal) {
      const params = new URLSearchParams({
        q: searchTerm,
        'resources[type]': 'collection',
        'resources[limit]': Math.min(limit, 10)
      });

      try {
        const response = await fetch(`${theme.routes.predictiveSearch}.json?${params}`, { signal });
        if (!response.ok) return [];

        const data = await response.json();
        return (data.resources.results.collections || []).map((collection) => ({
          handle: collection.handle,
          title: collection.title,
          image: (collection.featured_image && collection.featured_image.url) || ''
        }));
      } catch (error) {
        return [];
      }
    }

    /**
     * Gets the results for a search term.
     * @param {string} searchTerm - Search query.
     */
    async getResults(searchTerm) {
      if (PredictiveSearch.usesAlgolia() && searchTerm.length < PredictiveSearch.ALGOLIA_MIN_CHARS) {
        this.close();
        return;
      }

      this.setLiveRegionLoadingState();

      // Algolia first, falling back to Shopify's own predictive search below
      // if it is switched off, misconfigured or unreachable.
      const algoliaMarkup = await this.getAlgoliaResults(searchTerm);
      if (algoliaMarkup) {
        this.renderResults(algoliaMarkup);
        return;
      }

      let searchFields = 'title,product_type,variants.title,vendor';
      if (theme.settings.pSearchIncludeSkus) searchFields += ',variants.sku';
      if (theme.settings.pSearchIncludeTags) searchFields += ',tag';

      let searchParams = '';
      if (this.productTypeInput && this.productTypeInput.value !== '') {
        searchParams = `q=product_type:${encodeURIComponent(this.productTypeInput.value)} AND ${encodeURIComponent(searchTerm)}`;
      } else {
        searchParams = `q=${encodeURIComponent(searchTerm)}`;
      }

      searchParams += `&${encodeURIComponent('resources[limit]')}=${theme.settings.pSearchLimit}`;
      searchParams += `&${encodeURIComponent('resources[limit_scope]')}=${
        theme.settings.pSearchLimitScope
      }`;
      searchParams += `&${encodeURIComponent('resources[options][fields]')}=${searchFields}`;
      searchParams += '&section_id=predictive-search';

      const queryKey = searchParams.replace(' ', '-').toLowerCase();
      if (this.cachedResults[queryKey]) {
        this.renderResults(this.cachedResults[queryKey]);
        return;
      }

      try {
        const response = await fetch(`${theme.routes.predictiveSearch}?${searchParams}`);
        if (!response.ok) throw new Error(response.status);

        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();

        const resultsEl = tmpl.content.querySelector('#shopify-section-predictive-search');
        const resultsMarkup = resultsEl.innerHTML.replace(/psearch/g, this.input.id);

        this.cachedResults[queryKey] = resultsMarkup;
        this.renderResults(resultsMarkup);
      } catch (error) {
        this.close();
        throw error;
      }
    }

    /**
     * Sets the live region loading state.
     */
    setLiveRegionLoadingState() {
      this.setLiveRegionText(this.loadingText);
    }

    /**
     * Sets the live region text.
     * @param {string} statusText - Status text.
     */
    setLiveRegionText(statusText) {
      this.statusEl.setAttribute('aria-hidden', 'false');
      this.statusEl.textContent = statusText;

      setTimeout(() => {
        this.statusEl.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Renders the results markup.
     * @param {string} resultsMarkup - Results markup.
     */
    renderResults(resultsMarkup) {
      this.results.innerHTML = resultsMarkup;
      this.setAttribute('results', true);
      this.open();
    }

    /**
     * Opens the results listbox.
     */
    open() {
      this.overlay.classList.add('is-visible');
      if (this.getQuery().length) this.resetBtn.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.setAttribute('open', '');
      document.body.classList.add('overlay-predictive-search');

      // Add event handlers (so the bound event listeners can be removed).
      this.keydownHandler = this.keydownHandler || this.handleKeydown.bind(this);
      this.keyupHandler = this.keyupHandler || this.handleKeyup.bind(this);
      this.resetBtnClickHandler = this.resetBtnClickHandler || this.close.bind(this);
      this.overlayClickHandler = this.overlayClickHandler || this.close.bind(this);

      // Add event listeners (for while results are open).
      this.addEventListener('keydown', this.keydownHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.resetBtn.addEventListener('click', this.resetBtnClickHandler);
      this.overlay.addEventListener('click', this.overlayClickHandler);
    }

    /**
     * Closes the results listbox.
     * @param {object} evt - Event object.
     */
    close(evt) {
      // If reset button was clicked, empty the input field.
      if (evt && evt.target === this.resetBtn) {
        this.input.value = '';
        this.removeAttribute('results');
        this.input.focus();
      }

      // Deselect the selected result (if there is one).
      const selected = this.querySelector('.predictive-search__item[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', 'false');

      this.removeAttribute('open');
      this.resetBtn.hidden = true;
      this.overlay.classList.remove('is-visible');
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overlay-predictive-search');

      // Remove event listeners added on search results opening.
      this.removeEventListener('keydown', this.keydownHandler);
      this.resetBtn.removeEventListener('click', this.resetBtnClickHandler);
      this.overlay.removeEventListener('click', this.overlayClickHandler);
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
